import { DatePicker } from '@/components/DatePicker'
import { Rise } from '@/ui/core/motion'
import { format } from 'date-fns'
import { StepLayout } from '../StepLayout'
import {
	type DateState,
	MIN_BIRTH_YEAR,
	canProceed,
	getDateState,
	resolveButtonLabel,
} from '../logic/stepState'
import { PrimaryStepButton } from './PrimaryStepButton'
import type { StepProps } from './types'

const pad = (value: number) => String(value).padStart(2, '0')

/** `yyyy-MM-dd` from what the step holds, or null while it holds nothing usable. */
const toDay = (date: DateState) =>
	date.year.length === 4 && date.month && date.day
		? `${date.year}-${pad(Number(date.month))}-${pad(Number(date.day))}`
		: null

/** Back to the three parts the answers are sent as. */
const toParts = (day: string): DateState => ({
	year: day.slice(0, 4),
	month: day.slice(5, 7),
	day: day.slice(8, 10),
})

/**
 * A date, picked off the brand's own calendar.
 *
 * Picked rather than typed, and on our calendar rather than the platform's wheel: the wheel is
 * three spinning columns that cannot be read at a glance and looks like nothing else in the app,
 * where this is the grid the booking flow already uses. The month's name opens its years, which
 * is what makes a birth date reachable.
 *
 * Nothing invalid can be chosen, so there is no error line to show, and none written back
 * either: every day on the grid is a real day inside the range the step allows, and the one that
 * is chosen is the one wearing the mark.
 */
export const DateStep = ({
	step,
	state,
	onChange,
	onNext,
	onBack,
	onClose,
	progress,
	isSubmitting,
	isBirthDate = false,
}: StepProps<DateState> & { isBirthDate?: boolean }) => {
	const date = getDateState(state)
	const chosen = toDay(date)
	const today = format(new Date(), 'yyyy-MM-dd')
	// A birth date runs back to the earliest the form will take; anything else is a date in recent
	// memory, so its years stop at a decade and the grid opens on a month worth scrolling.
	const earliest = isBirthDate
		? `${MIN_BIRTH_YEAR}-01-01`
		: `${new Date().getFullYear() - 10}-01-01`

	return (
		<StepLayout
			title={step.title}
			description={step.description}
			progress={progress}
			onBack={onBack}
			onClose={onClose}
			cta={
				<PrimaryStepButton
					label={resolveButtonLabel(step, date)}
					onPress={onNext}
					disabled={!canProceed(step, date)}
					isLoading={isSubmitting}
				/>
			}
		>
			<Rise>
				<DatePicker
					value={chosen}
					onChange={(day) => onChange(toParts(day))}
					earliest={earliest}
					latest={today}
					// A birth date is navigated to by its number; which weekday it fell on is nothing
					// anyone is looking for, so the initials come off.
					weekdays={!isBirthDate}
				/>
			</Rise>
		</StepLayout>
	)
}
